import React from "react";
import { EditCustomerProps } from "../types";
import { useEditCustomer } from "../hooks/useEditCustomer";
import { useKeyboardNavigation } from "../hooks/useKeyboardNavigation";
import { useCustomerValidation } from "../hooks/useCustomerValidation";
import GenericForm, { FormFieldConfig } from "src/shared/components/genericform/GenericForm";
import LoadingScreen from "src/shared/components/LoadingScreen";

const EditCustomer: React.FC<EditCustomerProps> = (props) => {
  const {
    formData,
    isDuplicatePhone,
    isLoading,
    handleNameChange,
    handlePhoneChange,
    handleAlternativePhoneChange,
    handleEmailChange,
    handleAddress1Change,
    handleAddress2Change,
    handlePincodeChange,
    updateCustomer,
    closeEditCustomer,
  } = useEditCustomer(props);

  const { inputRefs } = useKeyboardNavigation();

  const originalData = {
    name: props.name,
    phone: props.phone,
    alternativephone: props.alternativephone,
    email: props.email,
    address: props.address,
    address2: props.address2,
    address3: props.address3,
  };

  const validation = useCustomerValidation({ formData, originalData });
  const isFormValid = typeof validation.isFormValid === "function" ? validation.isFormValid : () => Boolean(validation.isFormValid);

  const handleEnterFocus =
    (nextRef?: React.RefObject<HTMLInputElement>, submit?: boolean) =>
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (submit) {
          if (isFormValid() && !isLoading) updateCustomer();
        } else {
          nextRef?.current?.focus();
        }
      }
    };

  const adaptChange =
    (handler: (e: React.ChangeEvent<HTMLInputElement>) => void) =>
    (val: string) => {
      if (!isLoading) {
        handler({ target: { value: val } } as React.ChangeEvent<HTMLInputElement>);
      }
    };

  const fields: FormFieldConfig[] = [
    {
      id: "name",
      label: "Name",
      required: true,
      value: formData.name,
      onChange: adaptChange(handleNameChange),
      onKeyDown: handleEnterFocus(inputRefs.phoneNumberRef),
      disabled: isLoading,
    },
    {
      id: "phone",
      label: "Phone number",
      required: true,
      value: formData.phoneNumber,
      onChange: adaptChange(handlePhoneChange),
      inputRef: inputRefs.phoneNumberRef,
      onKeyDown: handleEnterFocus(inputRefs.emailRef),
      disabled: isLoading,
    },
    {
      id: "alternative-phone",
      label: "Alternative Phone number",
      value: formData.alternativePhone,
      onChange: adaptChange(handleAlternativePhoneChange),
    },
    {
      id: "email",
      label: "Email",
      value: formData.email,
      onChange: adaptChange(handleEmailChange),
      inputRef: inputRefs.emailRef,
      onKeyDown: handleEnterFocus(inputRefs.streetRef),
      disabled: isLoading,
    },
    {
      id: "address-section",
      label: "Address (Optional)",
      type: "section",
    },
    {
      id: "address1",
      label: "Street/Village",
      variant: "standard",
      value: formData.address1,
      onChange: adaptChange(handleAddress1Change),
      inputRef: inputRefs.streetRef,
      onKeyDown: handleEnterFocus(inputRefs.stateRef),
      disabled: isLoading,
    },
    {
      id: "address2",
      label: "District/State",
      variant: "standard",
      value: formData.address2,
      onChange: adaptChange(handleAddress2Change),
      inputRef: inputRefs.stateRef,
      onKeyDown: handleEnterFocus(inputRefs.pincodeRef),
      disabled: isLoading,
    },
    {
      id: "address3",
      label: "Pincode/Zipcode",
      variant: "standard",
      value: formData.address3,
      onChange: adaptChange(handlePincodeChange),
      inputRef: inputRefs.pincodeRef,
      onKeyDown: handleEnterFocus(undefined, true),
      disabled: isLoading,
    },
  ];

  return (
    <>
      <GenericForm
        open={true}
        title="Edit customer details"
        fields={fields}
        onClose={closeEditCustomer}
        onSubmit={updateCustomer}
        isFormValid={isFormValid}
        confirmLabel="Update"
        cancelLabel="Cancel"
        isLoading={isLoading}
      />

      {isLoading && <LoadingScreen />}
    </>
  );
};

export default EditCustomer;
